import { NavLink, Outlet } from 'react-router-dom';
import { Button, Container, cx } from '../../components/ui';
import { useAuth } from '../../lib/auth';

const PANELS = [
  { to: '/app', label: 'Overview', end: true },
  { to: '/app/subscription', label: 'Subscription' },
  { to: '/app/credentials', label: 'Credentials' },
  { to: '/app/templates', label: 'Templates' },
  { to: '/app/usage', label: 'QA runs' },
  { to: '/app/profile', label: 'Profile' },
] as const;

export function DashboardLayout() {
  const { user, signOut } = useAuth();

  return (
    <Container className="py-10">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-hull-800 pb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white">Dashboard</h1>
          <p className="mt-1 text-sm text-hull-400">{user?.email}</p>
        </div>
        <Button variant="secondary" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>

      <div className="mt-6 gap-8 lg:flex">
        <nav aria-label="Dashboard" className="lg:w-52 lg:shrink-0">
          <ul className="flex gap-1 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
            {PANELS.map((panel) => (
              <li key={panel.to}>
                <NavLink
                  to={panel.to}
                  end={'end' in panel ? panel.end : false}
                  className={({ isActive }) =>
                    cx(
                      'block whitespace-nowrap rounded-lg px-3 py-2 text-sm transition',
                      isActive
                        ? 'bg-hull-800 font-medium text-white'
                        : 'text-hull-300 hover:bg-hull-800/60 hover:text-white',
                    )
                  }
                >
                  {panel.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 flex-1 pt-4 lg:pt-0">
          <Outlet />
        </div>
      </div>
    </Container>
  );
}

/**
 * Every panel state spec 62 requires, in one place.
 *
 * The empty state takes a real message rather than defaulting to something
 * generic: "no data" tells a user nothing about what to do next.
 */
export function PanelState({
  loading,
  error,
  empty,
  emptyTitle,
  emptyBody,
  emptyAction,
  children,
}: {
  loading: boolean;
  error: unknown;
  empty?: boolean;
  emptyTitle?: string;
  emptyBody?: string;
  emptyAction?: React.ReactNode;
  children: React.ReactNode;
}) {
  if (loading) {
    return (
      <div className="space-y-3" aria-busy="true">
        {[0, 1, 2].map((row) => (
          <div key={row} className="h-20 animate-pulse rounded-card bg-hull-800/60" />
        ))}
      </div>
    );
  }

  if (error) {
    const message =
      error instanceof Error ? error.message : 'Something went wrong loading this.';
    return (
      <div role="alert" className="rounded-card border border-fail-600/40 bg-fail-600/10 p-6">
        <h2 className="text-base font-semibold text-fail-400">Could not load this panel</h2>
        <p className="mt-2 text-sm text-hull-300">{message}</p>
      </div>
    );
  }

  if (empty) {
    return (
      <div className="rounded-card border border-dashed border-hull-700 p-10 text-center">
        <h2 className="text-base font-semibold text-white">{emptyTitle}</h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-hull-400">
          {emptyBody}
        </p>
        {emptyAction && <div className="mt-6">{emptyAction}</div>}
      </div>
    );
  }

  return <>{children}</>;
}
